import React, { useState } from 'react';
import { Box, HStack, Icon, IconButton, Input, VStack } from '@chakra-ui/react';
import { TbGripVertical, TbPlus, TbTrash } from 'react-icons/tb';

import reorderByIndex from '../../../../support/helpers/reorderByIndex';
import SortableList from '../../../uiold/SortableList';
import { Option } from '../../../../runtypes/SignupFormDefinitionRuntype';

type OptionsEditorProps = {
  options: Array<Option>;
  onChange: (newOptions: Array<Option>) => void;
};

export default function OptionsEditor(props: OptionsEditorProps) {
  const { onChange } = props;
  const allOptions = props.options;
  const filteredOptions = allOptions.filter((option) => !option.deleted);
  return (
    <VStack align="stretch" spacing={0}>
      <SortableList
        items={filteredOptions.map((option) => {
          return (
            <OptionField
              key={option.id}
              value={option}
              onChange={(newOption) => {
                const newOptions = allOptions.map((option) => {
                  return option.id === newOption.id ? newOption : option;
                });
                onChange(newOptions);
              }}
              onDeletePress={() => {
                const newOption = { ...option, deleted: true };
                const newOptions = allOptions.map((option) => {
                  return option.id === newOption.id ? newOption : option;
                });
                onChange(newOptions);
              }}
            />
          );
        })}
        onSort={(oldIndex, newIndex) => {
          onOrderChange({
            onChange,
            options: filteredOptions,
            newIndex,
            oldIndex,
          });
        }}
      />
      <NewOptionField
        key="new"
        onSubmit={(newValue) => {
          const newId = Date.now().toString(36);
          const newOption = { id: newId, name: newValue };
          onChange([...allOptions, newOption]);
        }}
      />
    </VStack>
  );
}

function OptionField(props: {
  value: Option;
  onChange: (value: Option) => void;
  onDeletePress: () => void;
}) {
  const { value, onChange, onDeletePress } = props;
  return (
    <HStack backgroundColor="white" my={2}>
      <HStack w={8} alignSelf="stretch" justify="center" cursor="move">
        <Icon as={TbGripVertical} fontSize={16} />
      </HStack>
      <Box flex="1">
        <Input
          size="sm"
          value={value.name}
          onChange={({ target }) => onChange({ ...value, name: target.value })}
        />
      </Box>
      <IconButton
        size="sm"
        aria-label={t('Delete')}
        variant="ghost"
        color="gray.400"
        _hover={{ color: 'black', backgroundColor: 'gray.100' }}
        icon={<Icon as={TbTrash} fontSize={16} />}
        onClick={onDeletePress}
      />
    </HStack>
  );
}

function NewOptionField(props: { onSubmit: (value: string) => void }) {
  const { onSubmit } = props;
  const [value, setValue] = useState('');
  return (
    <HStack
      as="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(value);
        setValue('');
      }}
      backgroundColor="white"
    >
      <Box w={8} />
      <Box flex={1}>
        <Input
          size="sm"
          placeholder={t('New Choice')}
          value={value}
          onChange={({ target }) => setValue(target.value)}
        />
      </Box>
      <IconButton
        size="sm"
        type="submit"
        aria-label={t('Add')}
        variant="ghost"
        color="gray.400"
        _hover={{ color: 'black', backgroundColor: 'gray.100' }}
        icon={<Icon as={TbPlus} fontSize={16} />}
      />
    </HStack>
  );
}

function onOrderChange(params: {
  options: Array<Option>;
  oldIndex: number;
  newIndex: number;
  onChange: (newOptions: Array<Option>) => void;
}) {
  let { options, oldIndex, newIndex, onChange } = params;
  let newFieldSets = reorderByIndex(options, oldIndex, newIndex);
  onChange(newFieldSets);
}
